import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { uniqBy, keyBy } from 'lodash-es';
import { Page } from 'seagull';
import ImportWizard from '~/components/ImportWizard/ImportWizard';
import { routes } from '~/constants';
import { useIoContext, useStoreActions } from '~/contexts';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { serviceModel, serviceTemplateModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useAppConfig, useStoreStatic } from '~/store/store-context';
import { parseAndValidateAgreementCsv } from './parseAndValidateAgreementCsv';
const servicesEntity = 'service templates';
const contactsEntity = 'contacts';
const LEGACY_CSV_TEMPLATE_URL = 'https://storage.googleapis.com/anchor-statics/Anchor_Import_Agreements_2026-01-22.zip';
const CSV_TEMPLATE_URL_V2 = 'https://storage.googleapis.com/anchor-statics/Anchor_Import_Agreements_2026-05-02.zip';
const getServiceUniqueKey = (s)=>s.name;
function applyExistingServiceTemplateId(draft, existingServiceTemplates) {
    var _draft_serviceBundles;
    const applyExistingServiceTemplateId = (s)=>{
        const existingServiceTemplate = existingServiceTemplates[getServiceUniqueKey(s)];
        if (existingServiceTemplate) {
            s.serviceTemplateId = existingServiceTemplate.id;
        }
    };
    const applyExistingServiceTemplateIdForService = (service)=>{
        var _service_bundle;
        applyExistingServiceTemplateId(service);
        (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services.forEach(applyExistingServiceTemplateId);
    };
    draft.services.forEach(applyExistingServiceTemplateIdForService);
    (_draft_serviceBundles = draft.serviceBundles) === null || _draft_serviceBundles === void 0 ? void 0 : _draft_serviceBundles.bundles.forEach((param)=>{
        let { services } = param;
        return services.forEach(applyExistingServiceTemplateIdForService);
    });
}
/** Imports agreements from a CSV file and saves them as drafts. */ export default function ImportAgreements() {
    const history = useHistory();
    const { t: importTexts } = useLocaleKeys('pages.importAgreements');
    const { t: commonTexts } = useLocaleKeys('common');
    const title = importTexts.title();
    const { CUSTOM_PAGES, UPDATE_DEFAULT_AGREEMENT_TITLE } = useFeatureFlags();
    const csvTemplateUrl = CUSTOM_PAGES ? CSV_TEMPLATE_URL_V2 : LEGACY_CSV_TEMPLATE_URL;
    const { logBI, api } = useStoreActions();
    const { getNow } = useIoContext();
    const serviceTemplateApi = useServiceTemplateApi();
    const [currentSaveData, setCurrentSaveData] = useState();
    const { generateUniqueId } = useAppConfig();
    const { contacts, refresh: refreshContacts } = useContacts();
    const keyedServiceTemplatesRef = useRef({});
    const { getState } = useStoreStatic();
    useSetDocumentTitle(title);
    const createServices = async (servicesToSave, setServicesSaveData, withIncludedServiceRealTemplateId)=>{
        for (const [index, s] of servicesToSave.entries()){
            var _template_bundle;
            const template = serviceTemplateModel.fromService(s);
            await serviceTemplateApi.create(serviceTemplateModel.makeServiceTemplate(_object_spread({
                id: template.id,
                name: template.name,
                description: template.description,
                cost: template.cost,
                billing: template.billing
            }, template.bundle && withIncludedServiceRealTemplateId ? {
                bundle: _object_spread_props(_object_spread({}, template.bundle), {
                    services: (_template_bundle = template.bundle) === null || _template_bundle === void 0 ? void 0 : _template_bundle.services.map(withIncludedServiceRealTemplateId)
                })
            } : {})));
            setServicesSaveData(index + 1);
        }
    };
    const getAllNewServicesToCreate = (items)=>{
        const newServices = [];
        const newBundles = [];
        const newIncludedServicesAsServices = [];
        const addServiceIfNotExists = (s)=>{
            const service = serviceModel.fromDTO(s);
            if (service != null && !keyedServiceTemplatesRef.current[service.name]) {
                if (service.bundle) {
                    if (!newBundles.find((b)=>b.name === service.name)) {
                        newBundles.push(service);
                    }
                } else {
                    if (!newServices.find((s)=>s.name === service.name)) {
                        newServices.push(service);
                    }
                }
            }
        };
        items.forEach((param)=>{
            let { draft } = param;
            var _draft_serviceBundles;
            draft.services.forEach(addServiceIfNotExists);
            (_draft_serviceBundles = draft.serviceBundles) === null || _draft_serviceBundles === void 0 ? void 0 : _draft_serviceBundles.bundles.forEach((param)=>{
                let { services } = param;
                return services.forEach(addServiceIfNotExists);
            });
        });
        items.forEach((param)=>{
            let { includedServicesToCreateIfNotExists } = param;
            return includedServicesToCreateIfNotExists.forEach((includedService)=>{
                const service = serviceModel.fromDTO(includedService);
                if (service && !keyedServiceTemplatesRef.current[service.name] && !newIncludedServicesAsServices.find((s)=>s.name === service.name) && !newServices.find((s)=>s.name === service.name)) {
                    newIncludedServicesAsServices.push(service);
                }
            });
        });
        return {
            newServices,
            newBundles,
            newIncludedServicesAsServices
        };
    };
    const updateKeyedServiceTemplates = async ()=>{
        const serviceTemplates = await serviceTemplateApi.getActive();
        keyedServiceTemplatesRef.current = keyBy(serviceTemplates, getServiceUniqueKey);
    };
    const analyzeAndCreateAllRequiredServices = async (items)=>{
        await updateKeyedServiceTemplates();
        let savedServices = 0;
        const { newServices, newBundles, newIncludedServicesAsServices } = getAllNewServicesToCreate(items);
        const setServicesSaveData = (index)=>setCurrentSaveData({
                entity: servicesEntity,
                saved: savedServices + index,
                total: newServices.length + newIncludedServicesAsServices.length + newBundles.length
            });
        await createServices(newServices, setServicesSaveData);
        savedServices += newServices.length;
        await createServices(newIncludedServicesAsServices, setServicesSaveData);
        savedServices += newIncludedServicesAsServices.length;
        await updateKeyedServiceTemplates();
        await createServices(newBundles, setServicesSaveData, function withIncludedServiceRealTemplateId(includedService) {
            var _keyedServiceTemplatesRef_current_includedService_name;
            const existingServiceTemplateId = (_keyedServiceTemplatesRef_current_includedService_name = keyedServiceTemplatesRef.current[includedService.name]) === null || _keyedServiceTemplatesRef_current_includedService_name === void 0 ? void 0 : _keyedServiceTemplatesRef_current_includedService_name.id;
            return _object_spread_props(_object_spread({}, includedService), {
                id: existingServiceTemplateId !== null && existingServiceTemplateId !== void 0 ? existingServiceTemplateId : generateUniqueId('included-service')
            });
        });
        await updateKeyedServiceTemplates();
    };
    const analyzeAndCreateAllRequiredContacts = async (items)=>{
        await refreshContacts();
        const newContacts = items.map((i)=>i.contact);
        const uniqueNewContacts = uniqBy(newContacts, (c)=>c.uniqueKey());
        const existingContactsUniqueKeys = new Set(contacts.map((c)=>c.uniqueKey()));
        const contactsToSave = uniqueNewContacts.filter((c)=>!existingContactsUniqueKeys.has(c.uniqueKey()));
        let savedContacts = 0;
        const setContactsSaveData = (errorMsg)=>setCurrentSaveData({
                entity: contactsEntity,
                saved: savedContacts,
                total: contactsToSave.length,
                errorMsg
            });
        setContactsSaveData();
        for (const c of contactsToSave){
            await api.contact.create(c).catch((err)=>{
                const msg = new ErrorResponse(err).saveContactErrorText();
                setContactsSaveData(msg);
                throw err;
            });
            savedContacts++;
            setContactsSaveData();
        }
        // Refresh contacts to attach them later to the saved drafts.
        if (contactsToSave.length) {
            await refreshContacts();
        }
        setCurrentSaveData(undefined);
    };
    const onStartSave = async (items)=>{
        await analyzeAndCreateAllRequiredServices(items);
        await analyzeAndCreateAllRequiredContacts(items);
    };
    return /*#__PURE__*/ _jsx(Page, {
        skin: "secondary",
        header: title,
        children: /*#__PURE__*/ _jsx(ImportWizard, {
            entityName: "agreements",
            saveEntityName: "drafts",
            currentSaveData: currentSaveData,
            importBtnText: importTexts.importBtnText(),
            csvTemplateUrl: csvTemplateUrl,
            testId: "import-agreements",
            onStartSave: onStartSave,
            saveItem: (param)=>{
                let { draft, contact: newContact } = param;
                const contactUniqueKeyFromDraft = newContact.uniqueKey();
                const contact = contacts.find((c)=>c.uniqueKey() === contactUniqueKeyFromDraft);
                draft.clientContactId = contact ? contact.id() : null;
                applyExistingServiceTemplateId(draft, keyedServiceTemplatesRef.current);
                return api.draft.save(draft);
            },
            parseAndValidateCsv: async (data)=>{
                var _selectors_user_businessSettings;
                logBI(10901);
                const libraryLegalTermsId = (_selectors_user_businessSettings = selectors.user.businessSettings(getState())) === null || _selectors_user_businessSettings === void 0 ? void 0 : _selectors_user_businessSettings.legalTermsDocumentId;
                const availableLegalTerms = await api.business.getLegalTerms();
                const { items, errors } = await parseAndValidateAgreementCsv({
                    data,
                    availableLegalTerms,
                    now: getNow(),
                    generateUniqueId,
                    defaultDraftsValues: {
                        libraryLegalTermsId
                    },
                    defaultAgreementTitle: UPDATE_DEFAULT_AGREEMENT_TITLE ? commonTexts.defaultAgreementTitle() : commonTexts.untitledAgreement(),
                    customPagesEnabled: !!CUSTOM_PAGES
                });
                logBI(10902, {
                    numAgreements: items.length,
                    numErrors: errors.length
                });
                return {
                    items,
                    errors
                };
            },
            onEvent: (name)=>{
                if (name === 'csvTemplateDownload') {
                    logBI(10900);
                } else if (name === 'startImportItems') {
                    logBI(10903);
                } else if (name === 'redirectToItems') {
                    history.push(routes.draftsDashboard);
                } else if (name === 'afterSaveFailure') {
                    logBI(10908);
                } else if (name === 'afterSaveSuccess') {
                    logBI(10909);
                }
            }
        })
    });
}
